import { useEffect } from 'react'
import { create } from 'zustand'

export type ThemePreference = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'fundus.theme'

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // Private mode or blocked storage: fall back to following the OS.
  }
  return 'system'
}

/** Resolve a preference to the class the document should actually carry. */
export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference !== 'system') return preference
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyTheme(preference: ThemePreference) {
  const resolved = resolveTheme(preference)
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
}

interface ThemeState {
  preference: ThemePreference
  setPreference: (preference: ThemePreference) => void
}

export const useThemeStore = create<ThemeState>((set) => ({
  preference: readStoredPreference(),
  setPreference: (preference) => {
    try {
      localStorage.setItem(STORAGE_KEY, preference)
    } catch {
      // Not being able to remember the choice is not a reason to refuse it.
    }
    applyTheme(preference)
    set({ preference })
  },
}))

/**
 * Keep the document's theme class in sync on mount and whenever the OS
 * preference changes while "System" is selected. Mounted at the root, so every
 * shell — the desk and the shelf alike — gets it.
 */
export function useThemeSync() {
  const preference = useThemeStore((state) => state.preference)
  useEffect(() => {
    applyTheme(preference)
    if (preference !== 'system') return
    const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)')
    if (!media) return
    const onChange = () => applyTheme('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [preference])
}
